import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDateParams } from "@/shared/hooks/useDateParams";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { routeToView, type ViewMode } from "@/shared/config/routes";
import { useTimer } from "./hooks/useTimer";
import { useApplicationFilter } from "./hooks/useApplicationFilter";
import { useClientMatterFilter } from "./hooks/useClientMatterFilter";
import Sidebar from "./components/Sidebar/Sidebar";
import TrackedView, { TrackedViewHandle } from "./views/TrackedView";
import HistoryView from "./views/HistoryView";
import DeletedView, { DeletedViewHandle } from "./views/DeletedView";
import CalendarView from "@/v2/pages/Calendar";
import { buildDailySums } from "@/v2/lib/daySums";
import SettingsView from "./views/SettingsView";
import SupportView from "./views/SupportView/SupportView";
import FAQView from "@/shared/components/FAQ";
import UpdatesView from "@/shared/components/Updates/UpdatesView";
import DocsView from "@/shared/components/Docs/DocsView";
import ReportingView from "./views/ReportingView";
import ReportingDashboardView from "./views/ReportingDashboardView";
import styles from "./Attorney.module.css";
import HeaderBar from "./components/HeaderBar/HeaderBar";
import { ColumnPickerProvider } from "./components/HeaderBar/ColumnPickerContext";
import SummaryCards from "./components/SummaryCards/SummaryCards";
import { EntriesProvider, useEntries } from "@/shared/contexts/EntriesContext";
import { useSettingsCache, useMattersCache, useTasksCache, useActivitiesCache, useAbbreviationsCache, useCacheMutations } from "@/shared/cache";
import AddEntryV2, { AddEntryV2Handle } from "./components/v2/AddEntryV2";
import { ExportValidationDialog, InvalidMatter } from "./components/ExportValidationDialog";
import { ExportResultDialog, ExportResult } from "./components/ExportResultDialog";
import { useApi } from "@/shared/utils/request";
import { toCreateBody } from "@/v2/hooks/actions/add";
import { parseEntryId } from "@/v2/hooks/actions/entryId";
import { logger } from "@/shared/utils/logger";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/shared/utils/toast";
import ProcessingIndicator from "./components/ProcessingIndicator/ProcessingIndicator";
import { SidebarProvider, SidebarInset } from "@/shared/components/ui/sidebar";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useExportEntries } from "./hooks/useExportEntries";
import { useUpdateNotification } from "@/shared/hooks/useUpdateNotification";

function AttorneyPageContent() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { makeApiRequest } = useApi();
  const { hasNewUpdate, dismissUpdate } = useUpdateNotification();

  // Redirect to updates page on first load if there are unseen updates
  useEffect(() => {
    if (hasNewUpdate) {
      dismissUpdate();
      navigate("/updates", { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive viewMode from URL
  const viewMode: ViewMode = useMemo(() => {
    const segment = location.pathname.split('/').filter(Boolean)[0] || 'tracked';
    return routeToView[segment] || 'entries';
  }, [location.pathname]);

  const viewTitles: Record<ViewMode, string> = {
    entries: 'Tracked',
    history: 'Exported',
    deleted: 'Deleted',
    calendar: 'Calendar',
    settings: 'Settings',
    support: 'Support',
    faq: 'FAQ',
    updates: 'Updates',
    reporting: 'Reporting',
    team: 'Team',
    docs: 'Docs',
  };
  useDocumentTitle(viewTitles[viewMode]);

  // Application filter hook - persists filter state to localStorage
  const {
    isVisible: isApplicationVisible,
    toggleApplication,
    filterEntries,
    showAll: showAllApplications,
    hideAll: hideAllApplications,
  } = useApplicationFilter(user?.email || "");

  // Client/matter filter hook - persists filter state to localStorage per user
  const {
    clientFilter,
    matterFilter,
    setClientFilter,
    setMatterFilter,
    filterByClientMatter,
  } = useClientMatterFilter(user?.email || "");

  const [sortingMode, setSortingMode] = useState<'time'|'matter'>("time");
  const [sortDirection, setSortDirection] = useState<'asc'|'desc'>("desc");

  const handleSortByTime = useCallback(() => {
    if (sortingMode === "time") {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortingMode("time");
      setSortDirection("desc");
    }
  }, [sortingMode]);

  const handleSortByMatter = useCallback(() => {
    if (sortingMode === "matter") {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortingMode("matter");
      setSortDirection("asc");
    }
  }, [sortingMode]);
  const { entries, exportedEntries, deletedEntries, refreshRecords, isProcessingEntry, processingApplication } = useEntries();
  const { settings, updateSettings } = useSettingsCache();
  const { matters } = useMattersCache();
  const { tasks } = useTasksCache();
  const { activities } = useActivitiesCache();
  const { optimisticUpdate } = useCacheMutations();

  // Client/matter filter options — derived from entries (only show clients/matters
  // that actually appear in entries), with full display info looked up from the
  // matters cache. Mirrors useReportingDashboard's pattern. Matter list is scoped
  // by the currently-selected client so the dropdown narrows as you filter.
  const matterById = useMemo(() => {
    const map = new Map<string, (typeof matters)[number]>();
    for (const m of matters) map.set(m.matter_id, m);
    return map;
  }, [matters]);

  const filterableClients = useMemo(() => {
    const set = new Set<string>();
    for (const e of entries) if (e.client) set.add(e.client);
    for (const e of exportedEntries) if (e.client) set.add(e.client);
    for (const e of deletedEntries) if (e.client) set.add(e.client);
    return [...set].sort();
  }, [entries, exportedEntries, deletedEntries]);

  const filterableMatters = useMemo(() => {
    const map = new Map<string, { matterId: string; matterNumber: string; matterDescription: string; client: string }>();
    const consider = (e: { matter_id?: string; matter?: string; client?: string }) => {
      if (!e.matter_id || map.has(e.matter_id)) return;
      if (clientFilter && e.client !== clientFilter) return;
      const cached = matterById.get(e.matter_id);
      map.set(e.matter_id, {
        matterId: e.matter_id,
        matterNumber: cached?.matter || e.matter || "",
        matterDescription: cached?.description || "",
        client: cached?.client || e.client || "Unknown",
      });
    };
    for (const e of entries) consider(e);
    for (const e of exportedEntries) consider(e);
    for (const e of deletedEntries) consider(e);
    return [...map.values()].sort((a, b) => a.matterNumber.localeCompare(b.matterNumber));
  }, [entries, exportedEntries, deletedEntries, clientFilter, matterById]);

  // Date state from URL params
  const { date, dateRange, setDate, setDateRange } = useDateParams();

  // Cache-backed abbreviations
  const { abbreviationProps } = useAbbreviationsCache();

  // Shared add entry dialog for all views (moved up for timer callback)
  const addDialogV2Ref = useRef<AddEntryV2Handle>(null);

  // Timer hook - opens add entry dialog with calculated duration when stopped
  const handleTimerStop = useCallback((seconds: number) => {
    const roundedUnits = Math.ceil(seconds / 360) / 10; // Round to nearest 0.1 hour
    addDialogV2Ref.current?.openWithDuration(roundedUnits);
  }, []);

  const {
    elapsedSeconds,
    isRunning: timerRunning,
    start: startTimer,
    stop: stopTimer,
    reset: resetTimer,
  } = useTimer({ onStop: handleTimerStop });

  // Export validation dialog state (for Add and Export)
  const [exportValidationOpen, setExportValidationOpen] = useState(false);
  const [invalidMatters, setInvalidMatters] = useState<InvalidMatter[]>([]);
  const [pendingExportId, setPendingExportId] = useState<number | null>(null);
  const [exportResultOpen, setExportResultOpen] = useState(false);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);

  const { exportEntries: exportSingleEntry, isExporting: isSingleExporting } = useExportEntries({
    makeApiRequest,
    progressToastId: "export-progress-single",
    onComplete: ({ exportedIds, failedEntries }) => {
      refreshRecords();
      if (failedEntries.length > 0) {
        setExportResult({
          success: exportedIds.length > 0,
          records: exportedIds.map((id) => ({ id: String(id) })),
          failedEntries: failedEntries.map((f) => ({ id: String(f.id), error: f.error })),
          errorMessage: null,
        });
        setExportResultOpen(true);
      } else if (exportedIds.length > 0) {
        toast.success("Entry exported");
      }
    },
  });

  const performExport = async (entryId: number) => {
    await exportSingleEntry([entryId]);
  };

  const onSaveNewEntry = async (
    newEntry: {
      id: number; date: string; matter: string; matter_id: string; description: string; duration: number; task: string; activity: string;
    },
    shouldExport: boolean
  ) => {
    try {
      const resp = await makeApiRequest({
        endpoint: "/v2/entries",
        method: "POST",
        body: toCreateBody({ ...newEntry, end_time: new Date().toLocaleTimeString() }),
      });
      if (resp.error) throw new Error(String(resp.error));

      // Optimistically update last_used_matter in cache
      if (settings.auto_populate_last_used_matter) {
        optimisticUpdate('settings', (prev: any) => ({
          ...prev,
          last_used_matter: newEntry.matter_id,
        }));
      }

      // v1 export endpoints still take numeric ids — strip the "ent_" prefix.
      // Wrap so a malformed id (backend regression) surfaces as a partial-
      // success toast rather than the outer "Failed to add entry" — the
      // entry has already persisted and a retry would create a duplicate.
      let newEntryId: number | null = null;
      if (resp.data?.id) {
        try {
          newEntryId = parseEntryId(resp.data.id);
        } catch (err) {
          logger.captureException(err instanceof Error ? err : new Error(String(err)), {
            action: "addEntry-parseEntryId",
            id: resp.data.id,
          });
          if (shouldExport) {
            toast.error("Entry added but export failed", {
              duration: TOAST_DURATION.error,
              description: err instanceof Error ? err.message : undefined,
            });
          } else {
            toast.success("Entry added");
          }
          await refreshRecords();
          return;
        }
      }

      // If shouldExport, validate first then export
      if (shouldExport && newEntryId) {
        // Validate export
        try {
          const validationResp = await makeApiRequest({ endpoint: "/records/validateExport", method: "POST", body: { ids: [newEntryId] } });
          if (validationResp.data?.invalidMatters?.length > 0) {
            // Show validation dialog - user can choose to proceed or cancel
            setPendingExportId(newEntryId);
            setInvalidMatters(validationResp.data.invalidMatters);
            setExportValidationOpen(true);
            toast.success("Entry added");
            await refreshRecords();
            return; // Don't export yet - wait for user decision
          }
        } catch {
          // Validation failed silently - proceed with export
        }

        // No invalid matters - proceed with export
        const exportResp = await makeApiRequest({ endpoint: "/records/exportRecords", method: "POST", body: { ids: [newEntryId] } });
        if (exportResp.error) throw new Error(String(exportResp.error));
        const { failedEntries: addExportFailed = [] } = exportResp.data ?? {};
        if (addExportFailed.length > 0) {
          toast.error("Entry added but export failed", {
            duration: TOAST_DURATION.error,
            description: addExportFailed[0].error,
          });
        } else {
          toast.success("Entry added and exported");
        }
      } else {
        toast.success("Entry added");
      }

      await refreshRecords();
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'addEntry', shouldExport });
      }
      toast.error("Failed to add entry", {
        duration: TOAST_DURATION.error,
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  const updateHardcodedMatter = async (matterId: string) => {
    await updateSettings({ hardcoded_matter: matterId });
  };

  // Failed export state — lifted here so it survives TrackedView unmount on tab switch
  const [failedExportEntries, setFailedExportEntries] = useState<Map<number, string>>(new Map());

  // Selection + actions wired to TrackedView
  const [selectionCount, setSelectionCount] = useState(0);
  const [isExportingFromTracked, setIsExportingFromTracked] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<"combine" | "delete" | "duplicate" | "restore" | null>(null);
  const trackedRef = useRef<TrackedViewHandle>(null);
  const deletedRef = useRef<DeletedViewHandle>(null);
  const onAddEntry = () => {
    addDialogV2Ref.current?.openWithDuration(0);
  };
  const onCombine = () => trackedRef.current?.combineSelected();
  const onDuplicate = () => trackedRef.current?.duplicateSelected();
  const onDelete = () => trackedRef.current?.deleteSelected();
  const onExport = () => trackedRef.current?.exportSelected();
  const onUndo = () => deletedRef.current?.undoSelected();

  // Build daily sums for calendar
  const calendarDailySums = useMemo(
    () => buildDailySums(entries, exportedEntries),
    [entries, exportedEntries],
  );

  // Core attorney views (entries, history, deleted) share HeaderBar + bordered wrapper
  const isCoreView = viewMode === 'entries' || viewMode === 'history' || viewMode === 'deleted';

  const renderCoreContent = () => {
    switch (viewMode) {
      case "entries":
        return (
          <TrackedView
            ref={trackedRef}
            date={date}
            dateRange={dateRange}
            sortingMode={sortingMode}
            sortDirection={sortDirection}
            onSelectionChange={setSelectionCount}
            onExportingChange={setIsExportingFromTracked}
            onActionInProgressChange={setActionInProgress}
            onSortByMatter={handleSortByMatter}
            onSortByTime={handleSortByTime}
            filterEntries={filterEntries}
            filterByClientMatter={filterByClientMatter}
            failedExportEntries={failedExportEntries}
            setFailedExportEntries={setFailedExportEntries}
          />
        );
      case "history":
        return (
          <HistoryView
            date={date}
            dateRange={dateRange}
            sortingMode={sortingMode}
            sortDirection={sortDirection}
            onSortByMatter={handleSortByMatter}
            onSortByTime={handleSortByTime}
            filterEntries={filterEntries}
            filterByClientMatter={filterByClientMatter}
          />
        );
      case "deleted":
        return (
          <DeletedView
            ref={deletedRef}
            date={date}
            dateRange={dateRange}
            sortingMode={sortingMode}
            sortDirection={sortDirection}
            onSelectionChange={setSelectionCount}
            onActionInProgressChange={setActionInProgress}
            onSortByMatter={handleSortByMatter}
            onSortByTime={handleSortByTime}
            filterEntries={filterEntries}
            filterByClientMatter={filterByClientMatter}
          />
        );
      default:
        return null;
    }
  };

  const renderView = () => {
    switch (viewMode) {
      case "calendar":
        return <CalendarView dailySums={calendarDailySums} />;
      case "settings":
        return <SettingsView />;
      case "support":
        return <SupportView />;
      case "faq":
        return <FAQView />;
      case "updates":
        return <UpdatesView />;
      case "team":
        return <ReportingView />;
      case "reporting":
        return <ReportingDashboardView />;
      case "docs":
        return <DocsView />;
      default:
        return null;
    }
  };

  return (
    <SidebarProvider>
      <ProcessingIndicator
        show={isProcessingEntry}
        message="Processing entry..."
        application={processingApplication}
      />
      <AddEntryV2
        ref={addDialogV2Ref}
        onSave={onSaveNewEntry}
        matters={matters}
        tasks={tasks}
        activities={activities}
        currentDate={date}
        hardcodedMatter={settings.hardcoded_matter}
        lastUsedMatter={
          settings.auto_populate_last_used_matter && !settings.hardcoded_matter
            ? settings.last_used_matter || undefined
            : undefined
        }
        abbreviations={abbreviationProps}
      />
      <ExportValidationDialog
        open={exportValidationOpen}
        onOpenChange={setExportValidationOpen}
        invalidMatters={invalidMatters}
        onProceed={() => {
          setExportValidationOpen(false);
          if (pendingExportId) {
            performExport(pendingExportId);
          }
          setPendingExportId(null);
          setInvalidMatters([]);
        }}
        onCancel={() => {
          setExportValidationOpen(false);
          setPendingExportId(null);
          setInvalidMatters([]);
        }}
      />
      <ExportResultDialog
        open={exportResultOpen}
        onOpenChange={setExportResultOpen}
        result={exportResult}
        entries={entries}
      />
      <Sidebar />
      <SidebarInset className="bg-background overflow-hidden">
        <ColumnPickerProvider>
        {isCoreView ? (
          <div className="px-3 flex flex-col h-full overflow-hidden">
            <HeaderBar
              viewMode={viewMode as any}
              date={date}
              onDateChange={setDate}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              leftContent={<SummaryCards date={date} viewMode={viewMode as string} />}
              timerRunning={timerRunning}
              elapsedSeconds={elapsedSeconds}
              onStart={startTimer}
              onStop={stopTimer}
              onReset={resetTimer}
              selectionCount={selectionCount}
              onAddEntry={onAddEntry}
              onCombine={onCombine}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onExport={onExport}
              onUndo={onUndo}
              isUndoing={actionInProgress === "restore"}
              matters={matters}
              hardcodedMatter={settings.hardcoded_matter}
              onHardcodedMatterChange={updateHardcodedMatter}
              isApplicationVisible={isApplicationVisible}
              onToggleApplication={toggleApplication}
              onShowAllApplications={showAllApplications}
              onHideAllApplications={hideAllApplications}
              clientMatter={{
                clients: filterableClients,
                matters: filterableMatters,
                clientFilter,
                matterFilter,
                onClientChange: setClientFilter,
                onMatterChange: setMatterFilter,
              }}
              isExporting={isExportingFromTracked || isSingleExporting}
              actionInProgress={actionInProgress}
            />
            <div className={styles.reportContentWrapper}>
              <ErrorBoundary>
                {renderCoreContent()}
              </ErrorBoundary>
            </div>
          </div>
        ) : (
          <ErrorBoundary>
            {renderView()}
          </ErrorBoundary>
        )}
        </ColumnPickerProvider>
      </SidebarInset>
    </SidebarProvider>
  );
}

export default function AttorneyPage() {
  return (
    <EntriesProvider>
      <AttorneyPageContent />
    </EntriesProvider>
  );
}
